import axios from "axios";

import type { ApiTypes, EventType, Schemas } from "@fluid-app/api-client";
import {
  addCartItems,
  captureLead,
  createCart,
  createCartEnrollment,
  getCart,
  lookupAffiliate,
  removeCartItem,
  retrieveSettings,
  subscribeCartItem,
  unsubscribeCartItem,
  updateCartCountry,
  updateCartLanguage,
} from "@fluid-app/api-client";
import {
  flushEventsWithBeacon,
  getAttribution,
  getSessionToken,
  initializeFairshare,
  reset,
  trackFairshareEvent,
} from "@fluid-app/fairshare";
import {
  COOKIE_KEYS,
  getCookie,
  STORAGE_KEYS,
  StorageManager,
} from "@fluid-app/storage-manager";

import type { FluidConfig } from "../types";
import { debug } from "../../../fairshare/src/utils/debug";
import { setApiEndpoint } from "../utils/config";

/**
 * Core implementation of the Fluid SDK
 * Focuses on cart management functionality
 */
export class FluidCore {
  private static instance: FluidCore | null = null;
  private initialized = false;
  private storageManager: StorageManager;

  // Store country code for cart operations
  private countryCode: string | null = null;

  // Track cart item count
  private cartItemCount = 0;

  // Reference to cart count element to avoid repeated DOM queries
  private cartCountElement: HTMLElement | null = null;

  // Cart API related state (moved from CartApiClient class)
  private fluidShop = "";

  // Track pending cart operations
  private pendingOperations: Promise<unknown>[] = [];

  // Track settings loading state
  private settingsLoading: Promise<
    ApiTypes["settings-retrieve"]["response"] | null
  > | null = null;

  private constructor() {
    // Initialize storage manager
    this.storageManager = new StorageManager();
  }

  private dispatchEvent = (eventName: EventType) => {
    window.dispatchEvent(new CustomEvent(eventName as string));
  };

  private getCartToken(): string | null {
    const cart = this.storageManager.getObject<Schemas["Cart"]>(
      STORAGE_KEYS.CART,
    );
    return cart?.cart_token ?? null;
  }

  /**
   * Create a new cart
   * @param request Cart creation request data
   * @returns Promise resolving to the cart creation response
   */
  private async createCart(
    request: Parameters<typeof createCart>[number]["request"],
  ): ReturnType<typeof createCart> {
    console.log("Creating cart", request);
    this.ensureInitialized();

    try {
      // Create cart via API
      const response = await createCart({
        request,
      });

      if (response.cart?.cart_token) {
        this.saveCartToken(response.cart.cart_token);
      }

      // Save cart data if available
      if (response.cart) {
        this.saveCart(response.cart);
        this.dispatchEvent("CREATE_CART");
      }

      return response;
    } catch (error) {
      console.error("Error creating cart", error);
      throw error;
    }
  }

  async updateCartItems(
    items: ApiTypes["cart_items-add"]["request"]["items"],
    countryCode?: string,
  ): Promise<void> {
    this.ensureInitialized();

    const cartToken = this.getCartToken();
    if (!cartToken) {
      throw new Error("No cart token available. Create a cart first.");
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Create an optimistically updated cart
      const updatedItems = [...currentCart.items];

      // Update quantities for existing items or add new items
      for (const item of items) {
        const existingItemIndex = updatedItems.findIndex(
          (i) => i.variant?.id === item.variant_id,
        );

        if (existingItemIndex >= 0) {
          // Update existing item
          updatedItems[existingItemIndex] = {
            ...updatedItems[existingItemIndex],
            quantity: item.quantity,
          };
        }
      }

      // Save optimistically updated cart to local storage
      this.saveCart({
        ...currentCart,
        items: updatedItems,
      });

      // Dispatch event for optimistic update
      this.dispatchEvent("UPDATE_CART_ITEM");
    }

    // Make the actual API call and track it as a pending operation
    const operation = this.addCartItems({
      items,
      countryCode,
      event: "UPDATE_CART_ITEM",
    });

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Wait for the operation to complete
    await operation;
  }

  /**
   * Add items to the cart
   * @param options The options for adding items to cart
   * @returns Promise resolving to the updated cart
   */
  async addCartItems({
    items,
    countryCode,
    event = "ADD_TO_CART",
  }: {
    items: (ApiTypes["cart_items-add"]["request"]["items"][0] & {
      subscribe?: boolean;
    })[];
    countryCode?: string;
    event?: EventType;
  }): Promise<ApiTypes["cart_items-add"]["response"]["cart"]> {
    this.ensureInitialized();

    // Get country code from parameter, stored value, or default
    const effectiveCountryCode = countryCode ?? this.getCountryCode();

    // Process items to ensure each has a quantity (default to 1 if not specified)
    const processedItems = items.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity || 1,
    }));

    // Process subscription preferences from items
    const itemsWithSubscription = items.filter(
      (item) => item.subscribe === true,
    );

    // Check if cart token exists
    let cartToken = this.getCartToken();

    // If no cart token, create a new cart first
    if (!cartToken) {
      console.log("No cart token found, creating new cart");
      try {
        // Create a new cart with these items
        const createCartResponse = await this.createCart({
          country_code: effectiveCountryCode,
          items: processedItems,
          ...this.buildMetadata(),
        });

        // Handle subscription preferences after cart creation
        if (createCartResponse.cart && itemsWithSubscription.length > 0) {
          cartToken = createCartResponse.cart.cart_token ?? "";
          if (cartToken) {
            // Process subscriptions for newly created cart
            for (const item of itemsWithSubscription) {
              const cartItem = createCartResponse.cart.items?.find(
                (i) => i.variant?.id === item.variant_id,
              );
              if (cartItem?.id) {
                // Update the subscription status for this item
                await this._setItemSubscription(cartItem.id, true);
              }
            }
            // Refresh cart after subscription updates
            const updatedCart = await this.getCart();
            if (updatedCart) {
              return updatedCart;
            }
          }
        }

        // Return the newly created cart
        if (createCartResponse.cart) {
          return createCartResponse.cart;
        }
      } catch (e) {
        const err = e as Error;
        console.error("Error creating cart", err);
        throw new Error("Failed to create a cart");
      }

      // Check again for cart token after creation attempt
      cartToken = this.getCartToken();
      if (!cartToken) {
        throw new Error("Failed to create a cart - no cart token available");
      }
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Create an optimistically updated cart
      const updatedItems = [...currentCart.items];

      // Add or update items in the cart
      for (const newItem of processedItems) {
        // Only find regular (non-enrollment) items with the matching variant_id
        // This prevents updating enrollment pack items when adding regular items
        const existingIndex = updatedItems.findIndex(
          (item) => item.variant?.id === newItem.variant_id && !item.enrollment,
        );

        if (existingIndex >= 0) {
          // Update existing item quantity
          updatedItems[existingIndex] = {
            ...updatedItems[existingIndex],
            quantity: newItem.quantity,
          };
        }
      }

      // Save the optimistically updated cart locally
      this.saveCart({
        ...currentCart,
        items: updatedItems,
      });
    }

    try {
      // Dispatch the appropriate event
      this.dispatchEvent(event);

      // Make the API call to add the items
      const response = await addCartItems({
        params: { cart_token: cartToken },
        request: { items: processedItems, metadata: this.buildMetadata() },
      });

      // Save the cart with full data from the API
      if (response.cart) {
        this.saveCart(response.cart);

        // Process subscription preferences for newly added items
        if (itemsWithSubscription.length > 0) {
          for (const item of itemsWithSubscription) {
            const cartItem = response.cart.items?.find(
              (i) => i.variant?.id === item.variant_id,
            );
            if (cartItem?.id && !cartItem.subscription) {
              // Update the subscription status for this item
              await this._setItemSubscription(cartItem.id, true);
            }
          }
          // Refresh cart after subscription updates
          const updatedCart = await this.getCart();
          if (updatedCart) {
            return updatedCart;
          }
        }
      }

      return response.cart;
    } catch (e) {
      const err = e as Error;
      console.error("Error adding items to cart", err);
      throw new Error("Failed to add items to cart");
    }
  }

  /**
   * Helper method to generate common query parameters
   * @returns Object with common query parameters
   */
  private getCommonQueryParams() {
    return {
      fluid_shop: this.fluidShop,
      fluid_session: getSessionToken(),
    };
  }

  /**
   * Get the current cart
   * @returns Promise resolving to the cart response
   */
  async getCart(): Promise<ApiTypes["cart-get"]["response"]["cart"]> {
    this.ensureInitialized();

    const cartToken = this.getCartToken();
    // Check if we have a cart token
    if (!cartToken) {
      return undefined;
    }

    try {
      // Get cart via API
      const response = await getCart({
        params: {
          cart_token: cartToken,
        },
        query: this.getCommonQueryParams(),
      });

      // Save cart data if available
      if (response.cart) {
        this.saveCart(response.cart);
      }

      return response.cart;
    } catch (error) {
      console.error("Error getting cart", error);

      // Check if this is an axios error with a 410 status code (Gone - cart has been submitted)
      if (axios.isAxiosError(error) && error.response?.status === 410) {
        console.log(
          "Cart has been submitted and is no longer available. Clearing cart token.",
        );
        this.clearCart();
        return undefined;
      }

      throw error;
    }
  }

  public static getInstance(): FluidCore {
    FluidCore.instance ??= new FluidCore();
    return FluidCore.instance;
  }

  /**
   * Set a custom storage manager
   * @param storageManager Custom storage manager implementation
   */
  public setStorageManager(storageManager: StorageManager): void {
    this.storageManager = storageManager;
  }

  /**
   * Set the fluidShop identifier for API requests
   * @param fluidShop - The identifier for the shop
   */
  public setFluidShop(fluidShop: string): void {
    if (!fluidShop) {
      throw new Error("fluidShop cannot be empty");
    }
    this.fluidShop = fluidShop;
  }

  public initializeFluid(config: FluidConfig): void {
    if (this.initialized) {
      return;
    }

    // Validate that fluidShop is provided
    if (!config.fluidShop) {
      throw new Error("fluidShop is required in the configuration");
    }

    // Set custom API endpoint if provided
    if (config.apiEndpoint) {
      // Import and use the setApiEndpoint function
      setApiEndpoint(config.apiEndpoint);
    }

    // Store the configuration in localStorage for other components to access
    if (typeof window !== "undefined") {
      localStorage.setItem("fs_config", JSON.stringify(config));
    }

    // Check for country and locale in cookies and store in localStorage
    if (typeof window !== "undefined") {
      try {
        // Check for country code in cookies
        const countryCode = getCookie(COOKIE_KEYS.COUNTRY);
        if (countryCode) {
          this.storageManager.setItem(STORAGE_KEYS.COUNTRY, countryCode);
          this.countryCode = countryCode;
        }

        // Check for locale in cookies
        const locale = getCookie(COOKIE_KEYS.LOCALE);
        if (locale) {
          this.storageManager.setItem(STORAGE_KEYS.LOCALE, locale);
        }
      } catch (error) {
        console.error("Error reading country/locale cookies:", error);
      }
    }

    // Set the fluidShop before initializing FairShare
    this.setFluidShop(config.fluidShop);

    // Mark as initialized
    this.initialized = true;

    // Initialize cart item count
    const cart = this.getLocalCart();
    if (cart) {
      this.updateCartItemCount(cart);
    }

    // Initialize FairShare and check cart status after initialization
    try {
      // initializeFairshare now returns a Promise
      const fairsharePromise: Promise<void> = initializeFairshare(config);

      // Check if we need to validate a cart
      if (typeof window !== "undefined") {
        const cartToken = this.getCartToken();
        if (cartToken) {
          // Use void to safely handle the promise chain
          void (async () => {
            try {
              await fairsharePromise;
              await this.getCart();
            } catch {
              // Errors already handled in getCart
            }
          })();
        }
      }

      // Fetch settings only after FairShare initialization completes
      // This ensures the session token is available for the settings API call
      void fairsharePromise
        .then(() => {
          void this.fetchSettings();
        })
        .catch((error) => {
          console.error(
            "Error during FairShare initialization, skipping settings fetch:",
            error,
          );
        });
    } catch (error) {
      console.error(
        "Error initializing FairShare:",
        error instanceof Error ? error.message : String(error),
      );
      // Continue even if FairShare initialization fails
    }
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error("Fluid SDK not initialized. Call init() first.");
    }
  }

  /**
   * Save cart data to storage
   */
  private saveCart(cart: Schemas["Cart"]): void {
    try {
      this.storageManager.setObject<Schemas["Cart"]>(STORAGE_KEYS.CART, cart);

      // Update cart item count when saving cart
      this.updateCartItemCount(cart);
    } catch (error) {
      console.error("Error saving cart", error);
    }
  }

  /**
   * Save cart token to storage
   */
  private saveCartToken(token: string): void {
    this.storageManager.setObject<Schemas["Cart"]>(STORAGE_KEYS.CART, {
      ...this.storageManager.getObject<Schemas["Cart"]>(STORAGE_KEYS.CART),
      cart_token: token,
      attribution: null, // TODO: Get this from the API response
      recurring: [],
    });
  }

  // Cart Management Methods
  public clearCart(): void {
    this.ensureInitialized();
    this.storageManager.removeItem(STORAGE_KEYS.CART);

    // Reset cart item count
    this.cartItemCount = 0;
    this.updateCartCountElements();
  }

  public reset(): void {
    void reset();
    this.clearCart();
    this.initialized = false;
  }

  // Method to add a pending operation
  private addPendingOperation(operation: Promise<unknown>): void {
    const trackedOperation = operation.catch((error) => {
      console.error("Cart operation failed:", error);
    });

    // Add to pending operations
    this.pendingOperations.push(trackedOperation);

    // Clean up completed operations
    void trackedOperation.finally(() => {
      // Safely remove the operation from the array
      const index = this.pendingOperations.indexOf(trackedOperation);
      if (index !== -1) {
        // Create a new array without the completed operation
        this.pendingOperations = this.pendingOperations.filter(
          (op) => op !== trackedOperation,
        );
      }
    });
  }

  public async checkout(): Promise<void> {
    this.ensureInitialized();

    try {
      // Wait for all pending cart operations to complete
      if (this.pendingOperations.length > 0) {
        console.log(
          `Waiting for ${this.pendingOperations.length} pending cart operations to complete...`,
        );
        // Create a copy of the array to avoid linter warning
        const pendingOps = this.pendingOperations.slice();
        await Promise.all(pendingOps);
      }

      // Get the latest cart data to ensure checkout has the most recent state
      try {
        const freshCart = await this.getCart();
        if (freshCart) {
          this.saveCart(freshCart);
        }
      } catch (error) {
        console.error(
          "Error refreshing cart before checkout:",
          error instanceof Error ? error.message : String(error),
        );
        // Continue with checkout even if refresh fails
      }

      // Get cart token
      const cartToken = this.getCartToken();
      if (!cartToken) {
        throw new Error("No cart token available. Create a cart first.");
      }

      // Get attribution from FairShare
      const attribution = getAttribution();
      const attributionData = attribution?.shareGuid?.identifier ?? null;
      console.log("Attribution share GUID:", attributionData);

      // Track the checkout event
      trackFairshareEvent({
        eventName: "CHECKOUT_STARTED",
        data: {
          cart_token: cartToken,
          metadata: {
            fluid_shop: this.fluidShop,
            fluid_session: getSessionToken(),
            attribution: attributionData
              ? {
                  share_guid: attributionData,
                }
              : undefined,
          },
        },
      });

      // Wait for the event to be processed before redirecting
      // Use beacon for reliable delivery before redirect
      await flushEventsWithBeacon();

      // Redirect to checkout page
      window.location.href = `https://checkout.fluid.app/checkouts/${cartToken}`;
    } catch (error) {
      console.error(
        "Error during checkout:",
        error instanceof Error ? error.message : String(error),
      );
      throw error;
    }
  }

  /**
   * Remove an item from the cart
   * @param itemId The ID of the item to remove
   * @returns Promise that resolves when the item is removed
   */
  async removeCartItem(itemId: number): Promise<void> {
    this.ensureInitialized();

    const cartToken = this.getCartToken();
    if (!cartToken) {
      throw new Error("No cart token available. Create a cart first.");
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Create an optimistically updated cart by filtering out the item to remove
      const updatedItems = currentCart.items.filter(
        (item) => item.id !== itemId,
      );

      // Save optimistically updated cart to local storage
      this.saveCart({
        ...currentCart,
        items: updatedItems,
      });

      // Dispatch event for optimistic update
      this.dispatchEvent("REMOVE_FROM_CART");
    }

    // Create and track the operation
    const operation = (async () => {
      try {
        // Remove the item via API
        await removeCartItem({
          params: {
            cart_token: cartToken,
            item_id: itemId.toString(),
          },
          query: this.getCommonQueryParams(),
        });

        // Get updated cart to update totals
        const updatedCart = await this.getCart();
        if (updatedCart) {
          this.saveCart(updatedCart);
        }

        // We already dispatched the event for optimistic update
        return;
      } catch (error) {
        console.error("Error removing cart item", error);

        // On error, refresh cart from server to ensure consistency
        try {
          const freshCart = await this.getCart();
          if (freshCart) {
            this.saveCart(freshCart);
          }
        } catch (refreshError) {
          console.error(
            "Error refreshing cart after removal failure",
            refreshError,
          );
        }

        // If the cart wasn't found, clear the token
        if (error instanceof Error && error.message.includes("404")) {
          this.clearCart();
        }

        throw error;
      }
    })();

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Return the operation promise
    return operation;
  }

  /**
   * Get the cart data from local storage without making an API call
   * This is useful for optimistic updates
   * @returns The cart data from local storage or null if not found
   */
  public getLocalCart(): Schemas["Cart"] | null {
    // Check initialization status but don't throw an error
    // This allows components to safely try to get the cart before initialization
    if (!this.initialized) {
      console.warn(
        "Fluid SDK not fully initialized yet when calling getLocalCart",
      );
      return null;
    }

    try {
      // Get cart from local storage
      const cart = this.storageManager.getObject<Schemas["Cart"]>(
        STORAGE_KEYS.CART,
      );

      // Update cart count when getting from storage
      if (cart && this.cartItemCount === 0) {
        this.updateCartItemCount(cart);
      }

      return cart;
    } catch (error) {
      console.error("Error getting cart from local storage", error);
      return null;
    }
  }

  /**
   * Build metadata for API requests
   * @returns Metadata object with session, attribution, and other data
   */
  private buildMetadata(): Schemas["RequestMetadata"] {
    const sessionToken = getSessionToken();

    if (!sessionToken) {
      debug("Warning: No session token available when building metadata");
    } else {
      debug("Using session token for metadata:", sessionToken);
    }

    const metadata: Schemas["RequestMetadata"] = {
      fluid_session: sessionToken,
      fluid_shop: this.fluidShop,
    };

    // Get attribution data from FairShare (always fresh from current URL)
    try {
      const attribution = getAttribution();
      if (attribution?.shareGuid?.identifier) {
        metadata.attribution = {
          share_guid: attribution.shareGuid.identifier,
        };
        debug(
          "Using attribution from FairShare:",
          attribution.shareGuid.identifier,
        );
      }
    } catch (error) {
      console.error("Error getting attribution from FairShare:", error);
    }

    return metadata;
  }

  // Helper method to get country code
  private getCountryCode(): string {
    // First check if we have it stored in memory
    if (this.countryCode) {
      return this.countryCode;
    }

    // Then check localStorage
    const storedCountryCode = this.storageManager.getItem(STORAGE_KEYS.COUNTRY);
    if (storedCountryCode) {
      this.countryCode = storedCountryCode;
      return storedCountryCode;
    }

    // Default to US if not found
    return "US";
  }

  /**
   * Increment the quantity of an item in the cart, or add it if it doesn't exist.
   * @param itemsToIncrement The items to increment with their variant IDs, quantities, and subscription preferences
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed.
   */
  async incrementCartItem(
    itemsToIncrement: {
      variant_id: number;
      quantity: number;
      subscribe?: boolean;
    }[],
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized(); // Ensure SDK is initialized

    // Aggregate increments per variantId
    const incrementsMap = new Map<
      number,
      { quantity: number; subscribe?: boolean }
    >();
    for (const item of itemsToIncrement) {
      if (!item.variant_id || !item.quantity || item.quantity <= 0) {
        console.warn("Skipping invalid item in incrementCartItem:", item);
        continue;
      }

      const currentIncrement = incrementsMap.get(item.variant_id);
      incrementsMap.set(item.variant_id, {
        quantity: (currentIncrement?.quantity ?? 0) + item.quantity,
        subscribe: item.subscribe ?? currentIncrement?.subscribe,
      });
    }

    if (incrementsMap.size === 0) {
      console.warn("No valid items to increment.");
      return undefined;
    }

    // Get current cart state
    const currentCart = this.getLocalCart();

    // Calculate final quantities to set
    const finalItemsToSet: {
      variant_id: number;
      quantity: number;
      subscribe?: boolean;
    }[] = [];
    for (const [
      variantId,
      { quantity: totalIncrement, subscribe },
    ] of incrementsMap.entries()) {
      // Only find individual (non-enrollment) items with the matching variant_id
      // This prevents adding enrollment pack quantities to individual item quantities
      const currentItem = currentCart?.items?.find(
        (item) => item.variant?.id === variantId && !item.enrollment,
      );
      const currentQuantity = currentItem
        ? Number(currentItem.quantity) || 0
        : 0;
      const newQuantity = currentQuantity + totalIncrement;
      finalItemsToSet.push({
        variant_id: variantId,
        quantity: newQuantity,
        subscribe,
      });
    }

    // Always call addCartItems with the final calculated quantities.
    // It handles cart creation and updates internally.
    try {
      const updatedCart = await this.addCartItems({
        items: finalItemsToSet,
        event: "UPDATE_CART_ITEM", // Use UPDATE_CART_ITEM event type
      });
      return updatedCart;
    } catch (error) {
      console.error(
        `Error in incrementCartItem (via addCartItems) for items:`,
        finalItemsToSet,
        error,
      );
      // addCartItems should handle its own optimistic rollbacks if necessary
      return undefined; // Indicate failure
    }
  }

  /**
   * Decrement the quantity of an item in the cart, removing it if the quantity reaches zero or less.
   * @param variantId The variant ID of the item to decrement.
   * @param quantityToRemove The quantity to remove (defaults to 1).
   * @returns Promise resolving when the operation is complete. It might resolve to undefined if the item wasn't found.
   */
  async decrementCartItem(
    variantId: number,
    quantityToRemove = 1,
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();
    const cartToken = this.getCartToken(); // Ensure we have a cart context
    if (!cartToken) {
      console.warn("Cannot decrement item: No cart token available.");
      return undefined;
    }
    if (quantityToRemove <= 0) {
      console.warn("Cannot decrement item: quantityToRemove must be positive.");
      return undefined;
    }

    const currentCart = this.getLocalCart();
    // Only find individual (non-enrollment) items with the matching variant_id
    // This prevents modifying enrollment pack items when decrementing individual items
    const currentItem = currentCart?.items?.find(
      (item) => item.variant?.id === variantId && !item.enrollment,
    );

    if (!currentItem) {
      console.warn(
        `Cannot decrement item ${variantId}: Item not found in cart.`,
      );
      return undefined; // Item not in cart, nothing to do
    }

    const currentQuantity = Number(currentItem.quantity) || 0;
    const newQuantity = currentQuantity - quantityToRemove;

    try {
      if (newQuantity > 0) {
        // If quantity is still positive, update it using addCartItems
        const updatedCart = await this.addCartItems({
          items: [{ variant_id: variantId, quantity: newQuantity }],
          event: "UPDATE_CART_ITEM", // Or consider a new event type?
        });
        return updatedCart;
      } else {
        // If quantity is zero or less, remove the item using its cart item ID
        const itemId = currentItem.id; // Get the specific item ID for removal
        if (itemId === undefined) {
          // Simplified check
          console.error(
            `Cannot remove item with variant ID ${variantId}: Missing item ID.`,
          );
          return undefined;
        }
        await this.removeCartItem(itemId); // This handles its own API call and optimistic update
        // removeCartItem doesn't return the cart, maybe fetch it after?
        // For now, return undefined as the item is gone or state is handled by removeCartItem's saveCart
        const updatedCart = this.getLocalCart(); // Return the cart state after removal
        return updatedCart ?? undefined; // Convert null to undefined
      }
    } catch (error) {
      console.error(`Error decrementing item ${variantId}:`, error);
      // Existing methods should handle rollbacks
      return undefined; // Indicate failure
    }
  }

  /**
   * Add an enrollment pack to the cart
   * @param enrollmentPackId The ID of the enrollment pack to add
   * @param event Event type to dispatch (defaults to ADD_TO_CART)
   * @returns Promise resolving to the updated cart
   */
  async addEnrollmentPack({
    enrollmentPackId,
    event = "ADD_TO_CART",
  }: {
    enrollmentPackId: number;
    event?: EventType;
  }): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();

    // Check if cart token exists
    let cartToken = this.getCartToken();

    // If no cart token, create a new cart first
    if (!cartToken) {
      console.log("No cart token found, creating new cart");
      try {
        // Create a new cart first, then add the enrollment pack
        const metadata = this.buildMetadata();
        const createCartResponse = await this.createCart({
          country_code: this.getCountryCode(),
          ...metadata,
        });

        // Get the cart token from the response
        if (createCartResponse.cart?.cart_token) {
          cartToken = createCartResponse.cart.cart_token;
        } else {
          throw new Error("Failed to get cart token from new cart");
        }
      } catch (e) {
        const err = e as Error;
        console.error("Error creating cart before adding enrollment pack", err);
        throw new Error("Failed to create a cart for enrollment pack");
      }
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart) {
      // Dispatch the appropriate event for optimistic update
      this.dispatchEvent(event);
    }

    // Create and track the operation
    const operation = (async () => {
      try {
        // Make the API call to add the enrollment pack
        const response = await createCartEnrollment({
          params: { cart_token: cartToken },
          request: {
            enrollment_pack_id: enrollmentPackId,
            metadata: this.buildMetadata(),
          },
        });

        // Save the cart with full data from the API
        if (response.cart) {
          this.saveCart(response.cart);
        }

        return response.cart;
      } catch (e) {
        const err = e as Error;
        console.error("Error adding enrollment pack to cart", err);

        // Refresh the cart to ensure consistency
        try {
          const freshCart = await this.getCart();
          if (freshCart) {
            this.saveCart(freshCart);
            return freshCart;
          }
        } catch (refreshError) {
          console.error(
            "Error refreshing cart after enrollment pack error",
            refreshError,
          );
        }

        throw new Error("Failed to add enrollment pack to cart");
      }
    })();

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Return the operation promise
    return operation;
  }

  /**
   * Set a cart item's subscription status
   * @param itemId The ID of the item to update
   * @param subscribe Whether to subscribe (true) or unsubscribe (false)
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed
   */
  private async _setItemSubscription(
    itemId: number,
    subscribe: boolean,
  ): Promise<Schemas["Cart"] | undefined> {
    const cartToken = this.getCartToken();
    if (!cartToken) {
      console.error(
        "No cart token available for subscribing/unsubscribing item",
      );
      return undefined;
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Find the item to update
      const itemIndex = currentCart.items.findIndex(
        (item) => item.id === itemId,
      );

      if (itemIndex >= 0) {
        // Create a copy of the cart items
        const updatedItems = [...currentCart.items];

        // Update the subscription status
        updatedItems[itemIndex] = {
          ...updatedItems[itemIndex],
          subscription: subscribe,
        };

        // Save optimistically updated cart to local storage
        this.saveCart({
          ...currentCart,
          items: updatedItems,
        });

        // Dispatch event for optimistic update
        this.dispatchEvent("UPDATE_CART_ITEM");
      }
    }

    // Create and track the operation
    const operation = (async () => {
      try {
        // Subscribe or unsubscribe to the item via API
        let response;

        if (subscribe) {
          // Use subscribeCartItem from API client
          response = await subscribeCartItem({
            params: {
              cart_token: cartToken,
              item_id: itemId.toString(),
            },
            request: {
              metadata: this.buildMetadata(),
            },
          });
        } else {
          // Use unsubscribeCartItem from API client
          response = await unsubscribeCartItem({
            params: {
              cart_token: cartToken,
              item_id: itemId.toString(),
            },
            query: this.getCommonQueryParams(),
          });
        }

        // Update local cart with API response
        if (response.cart) {
          this.saveCart(response.cart);
          return response.cart;
        }

        return undefined;
      } catch (error) {
        console.error(
          `Error ${subscribe ? "subscribing to" : "unsubscribing from"} cart item`,
          error,
        );

        // On error, refresh cart from server to ensure consistency
        try {
          const freshCart = await this.getCart();
          if (freshCart) {
            this.saveCart(freshCart);
          }
        } catch (refreshError) {
          console.error(
            `Error refreshing cart after ${subscribe ? "subscription" : "unsubscription"} failure`,
            refreshError,
          );
        }

        throw error;
      }
    })();

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Return the operation promise
    return operation;
  }

  /**
   * Subscribe to a cart item
   * @param itemId The ID of the item to subscribe to
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed
   */
  async subscribeCartItem(
    itemId: number,
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();
    return this._setItemSubscription(itemId, true);
  }

  /**
   * Unsubscribe from a cart item
   * @param itemId The ID of the item to unsubscribe from
   * @returns Promise resolving to the updated cart, or undefined if the operation couldn't be completed
   */
  async unsubscribeCartItem(
    itemId: number,
  ): Promise<Schemas["Cart"] | undefined> {
    this.ensureInitialized();
    return this._setItemSubscription(itemId, false);
  }

  /**
   * Get the cart item count
   * @returns The current cart item count
   */
  public getCartItemCount(): number {
    return this.cartItemCount;
  }

  /**
   * Update the cart item count based on the cart contents
   * @param cart The cart to use for calculating the count
   */
  private updateCartItemCount(cart: Schemas["Cart"] | null): void {
    if (!cart?.items) {
      this.cartItemCount = 0;
    } else {
      this.cartItemCount = cart.items.reduce(
        (total, item) => total + (Number(item.quantity) || 0),
        0,
      );
    }

    // Update any elements with id "fluid-cart-count"
    this.updateCartCountElements();
  }

  /**
   * Update all elements with ID "fluid-cart-count" with the current cart count
   */
  private updateCartCountElements(): void {
    if (typeof window !== "undefined") {
      // Cache the element reference if we haven't already
      this.cartCountElement ??= document.getElementById("fluid-cart-count");

      if (this.cartCountElement) {
        this.cartCountElement.textContent = String(this.cartItemCount);
      }
    }
  }

  /**
   * Update locale settings for the cart
   * @param options The locale settings to update
   * @returns void
   */
  async updateLocaleSettings(options: { language?: string; country?: string }) {
    this.ensureInitialized();

    // Validate that at least one parameter is provided
    if (!options.language && !options.country) {
      throw new Error(
        "At least one locale parameter (language or country) must be provided",
      );
    }

    // Validate that parameters are non-empty strings if provided
    if (
      options.language &&
      (!options.language.trim() || typeof options.language !== "string")
    ) {
      throw new Error("Language must be a non-empty string");
    }

    if (
      options.country &&
      (!options.country.trim() || typeof options.country !== "string")
    ) {
      throw new Error("Country must be a non-empty string");
    }

    const cartToken = this.getCartToken();
    if (!cartToken) {
      throw new Error("No cart token available. Create a cart first.");
    }

    try {
      let updatedCart: Schemas["Cart"] | undefined;

      // Update language if provided
      if (options.language) {
        try {
          const languageResponse = await updateCartLanguage({
            params: { cart_token: cartToken },
            request: {
              language_iso: options.language,
              metadata: this.buildMetadata(),
            },
          });
          updatedCart = languageResponse.cart;
        } catch (error) {
          console.error(
            `Failed to update cart language to '${options.language}':`,
            error,
          );
          throw new Error(
            `Failed to update cart language: ${error instanceof Error ? error.message : String(error)}`,
          );
        }
      }

      // Update country if provided
      if (options.country) {
        try {
          const countryResponse = await updateCartCountry({
            params: { cart_token: cartToken },
            request: {
              country_code: options.country,
              metadata: this.buildMetadata(),
            },
          });
          updatedCart = countryResponse.cart;
        } catch (error) {
          console.error(
            `Failed to update cart country to '${options.country}':`,
            error,
          );
          throw new Error(
            `Failed to update cart country: ${error instanceof Error ? error.message : String(error)}`,
          );
        }
      }

      // Save the updated cart to local storage
      if (updatedCart) {
        this.saveCart(updatedCart);

        // Update stored country code if it was changed
        if (options.country) {
          this.countryCode = options.country;
        }

        // Dispatch the locale change event
        this.dispatchEvent("LOCALE_CHANGE");
      }

      return;
    } catch (error) {
      // If the error was already transformed by one of the inner catch blocks, just re-throw it
      if (
        error instanceof Error &&
        error.message.startsWith("Failed to update cart")
      ) {
        throw error;
      }

      // Otherwise, transform the generic error into a more specific one
      console.error("Error updating locale settings:", error);
      throw new Error(
        `Failed to update locale settings: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Capture a lead form submission
   * @param formData Lead capture form data
   * @returns Promise resolving to the lead capture response
   */
  async captureLead(
    formData: ApiTypes["lead-capture"]["request"]["lead"],
  ): Promise<ApiTypes["lead-capture"]["response"]> {
    this.ensureInitialized();

    try {
      // Add metadata to the request
      const request: ApiTypes["lead-capture"]["request"] = {
        lead: formData,
        metadata: this.buildMetadata(),
        page: {
          url: window.location.href,
          title: document.title,
          description:
            document
              .querySelector("meta[name='description']")
              ?.getAttribute("content") ?? undefined,
          image:
            document
              .querySelector("meta[property='og:image']")
              ?.getAttribute("content") ?? undefined,
        },
      };

      // Capture the lead via API
      const response = await captureLead({
        request,
      });

      return response;
    } catch (error) {
      console.error("Error capturing lead", error);
      throw error;
    }
  }

  /**
   * Fetches settings from the API and stores them in local storage
   * @returns Promise resolving to the settings or null if the operation failed
   */
  async fetchSettings(): Promise<
    ApiTypes["settings-retrieve"]["response"] | null
  > {
    this.ensureInitialized();

    // If there's already a settings fetch in progress, return that
    if (this.settingsLoading) {
      return this.settingsLoading;
    }

    // Create a new promise for the settings fetch
    this.settingsLoading = (async () => {
      try {
        // Build the request metadata
        const metadata = this.buildMetadata();
        const request: ApiTypes["settings-retrieve"]["request"] = {
          metadata,
          page: {
            url: window.location.href,
            title: document.title,
          },
        };

        // Make the API call
        const settings = await retrieveSettings({ request });

        // Store the settings in local storage
        this.storageManager.setObject(STORAGE_KEYS.SETTINGS, settings);

        // Dispatch event to notify components that settings are available
        window.dispatchEvent(new CustomEvent("SETTINGS_AVAILABLE"));

        // Clear the loading promise
        this.settingsLoading = null;

        return settings;
      } catch (error) {
        console.error("Error fetching settings:", error);

        // Clear the loading promise
        this.settingsLoading = null;

        return null;
      }
    })();

    return this.settingsLoading;
  }

  /**
   * Gets settings from local storage
   * @returns The settings from local storage or null if not found
   */
  getSettings<T = ApiTypes["settings-retrieve"]["response"]>(): T | null {
    return this.storageManager.getObject<T>(STORAGE_KEYS.SETTINGS);
  }

  /**
   * Gets settings from local storage, or fetches them from the API if not available
   * @returns Promise resolving to the settings or null if the operation failed
   */
  async getOrFetchSettings<
    T = ApiTypes["settings-retrieve"]["response"],
  >(): Promise<T | null> {
    // Try to get settings from local storage first
    const settings = this.getSettings<T>();
    if (settings) {
      return settings;
    }

    // If no settings in local storage, fetch from API
    const fetchedSettings = await this.fetchSettings();
    return fetchedSettings as T | null;
  }

  /**
   * Remove an enrollment pack from the cart
   * @param enrollmentPackId The ID of the enrollment pack to remove
   * @returns Promise that resolves when the enrollment pack is removed
   */
  async removeEnrollmentPack(enrollmentPackId: number): Promise<void> {
    this.ensureInitialized();

    const cartToken = this.getCartToken();
    if (!cartToken) {
      throw new Error("No cart token available. Create a cart first.");
    }

    // Get current cart from local storage for optimistic updates
    const currentCart = this.getLocalCart();
    if (currentCart?.items) {
      // Create an optimistically updated cart by filtering out all enrollment items
      const updatedItems = currentCart.items.filter((item) => !item.enrollment);

      // Save optimistically updated cart to local storage
      this.saveCart({
        ...currentCart,
        items: updatedItems,
      });

      // Dispatch event for optimistic update
      this.dispatchEvent("REMOVE_FROM_CART");
    }

    // Create and track the operation
    const operation = (async () => {
      try {
        // Try to remove the enrollment pack via API
        // Note: We use the same removeCartItem API but with the enrollment pack ID
        await removeCartItem({
          params: {
            cart_token: cartToken,
            item_id: enrollmentPackId.toString(),
          },
          query: this.getCommonQueryParams(),
        });

        // Get updated cart to update totals
        const updatedCart = await this.getCart();
        if (updatedCart) {
          this.saveCart(updatedCart);
        }

        // We already dispatched the event for optimistic update
        return;
      } catch (error) {
        console.error("Error removing enrollment pack", error);

        // Check if this is a 404 error specifically for the enrollment pack
        const is404Error =
          error instanceof Error &&
          (error.message.includes("404") ||
            error.message.includes("not found"));

        if (is404Error) {
          // For 404 errors, the enrollment pack might already be removed or doesn't exist
          // Don't clear the entire cart, just refresh to get the current state
          console.warn(
            `Enrollment pack ${enrollmentPackId} not found (404). It may have already been removed.`,
          );

          try {
            const freshCart = await this.getCart();
            if (freshCart) {
              this.saveCart(freshCart);
            }
          } catch (refreshError) {
            console.error(
              "Error refreshing cart after enrollment pack 404",
              refreshError,
            );
            // Only clear cart if we can't refresh it at all
            if (
              refreshError instanceof Error &&
              refreshError.message.includes("404")
            ) {
              this.clearCart();
            }
          }

          // Don't re-throw 404 errors for enrollment packs - treat as success
          return;
        }

        // For other errors, refresh cart from server to ensure consistency
        try {
          const freshCart = await this.getCart();
          if (freshCart) {
            this.saveCart(freshCart);
          }
        } catch (refreshError) {
          console.error(
            "Error refreshing cart after enrollment pack removal failure",
            refreshError,
          );

          // Only clear cart if the cart itself is not found
          if (
            refreshError instanceof Error &&
            refreshError.message.includes("404")
          ) {
            this.clearCart();
          }
        }

        // Re-throw non-404 errors
        throw error;
      }
    })();

    // Add this operation to pending operations
    this.addPendingOperation(operation);

    // Return the operation promise
    return operation;
  }

  /**
   * Look up affiliate information by attribution data
   * @param attribution The attribution object containing the lookup criteria (email, username, share_guid, fluid_rep_id, or external_id)
   * @returns Promise resolving to the affiliate lookup response
   */
  async lookupAffiliate(
    attribution: Schemas["Attribution"],
  ): Promise<ApiTypes["affiliate-lookup"]["response"]> {
    this.ensureInitialized();

    try {
      // Build the request metadata
      const metadata = this.buildMetadata();

      const request: ApiTypes["affiliate-lookup"]["request"] = {
        metadata: {
          ...metadata,
          attribution,
        },
      };

      // Make the API call
      const response = await lookupAffiliate({ request });

      return response;
    } catch (error) {
      console.error("Error looking up affiliate:", error);
      throw error;
    }
  }
}
